import { HttpErrorResponse } from '@angular/common/http';
import { ExceptionService } from '../services/exception.service';
import { Component, OnInit, Output, Input, EventEmitter, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TogetherDIPStageContainer } from './../models/app.models';
import { NodeAPIEntityService } from './../services/node.entity.service';
import * as _ from 'lodash-es';
import moment from 'moment';
import { DipStateService } from '../services/dip-state-service.service';
import { RecaptchaComponent } from 'ng-recaptcha';
import { environment } from 'environments/environment';

@Component({
    selector: 'together-auction-dip-login',
    templateUrl: './dip.login.component.html',
    providers: [NodeAPIEntityService],
    standalone: false
})
export class TogetherAuctionDIPLoginComponent {

  /**
   * The stage.
   * TODO: Remove and put into service.
   *
   * @type {TogetherDIPStageContainer}
   * @memberof TogetherAuctionDIPLoginComponent
   */
  @Input() stage: TogetherDIPStageContainer;

  /**
      * The case id of this case.
      *
      * @type {string}
      * @memberof TogetherAuctionDIPLoginComponent
      */
  @Input() incoming_case_id: string;

  /**
   * The stage changing output.
   * TODO: Move into service.
   *
   * @type {EventEmitter<string>}
   * @memberof TogetherAuctionDIPLoginComponent
   */
  @Output() stageChanging: EventEmitter<string> = new EventEmitter<string>();

  /**
   * Validate the date is correct.
   *
   * @param {AbstractControl} control
   * @memberof TogetherAuctionDIPLoginComponent
   */
  validateCorrectDate(): ValidatorFn {
    return (control: AbstractControl) => {

      // Get the control
      const ctrl = control as UntypedFormControl;

      // Get the date out of the control.
      const dateY = control.value;


      // Set errors to be added.
      const errors = {};

      // If the date is null, do nothing.
      if (_.isNil(dateY)) {
        return null;
      }

      // Date
      const date = moment(dateY, 'YYYY MM DD');

      // Dates as subtract on moment is iterative.
      const ageDate = moment([]);
      const elderlyDate = moment([]);

      // Get today's date.
      const today = moment().startOf('day');

      // Get the age cutoff for underage.
      const elderlyCutoff = elderlyDate.subtract(80, 'years');
      const ageCutoff = ageDate.subtract(18, 'years');

      // Get the diff
      const ageDiff = date.isSameOrBefore(ageCutoff);
      const elderlyDiff = date.isSameOrAfter(elderlyCutoff);

      // If the date is invalid.
      if (!date.isValid()) {
        errors['invalidDate'] = true;
      }

      // If there is an elderly age diff, prevent as too old to continue.
      if (!elderlyDiff) {
        errors['elderlyDate'] = true;
      }

      // If there is an age diff, add the error.
      if (!ageDiff) {
        errors['underageDate'] = true;
      }

      return _.isEmpty(errors) ? null : errors;
    };
  }

  /**
   * FormControl for the login date of birth day.
   *
   * @type {FormControl}
   * @memberof TogetherAuctionDIPLoginComponent
   */
  tadLoginDobDay: UntypedFormControl = new UntypedFormControl(null, [
    Validators.min(1),
    Validators.max(31),
    Validators.minLength(1),
    Validators.maxLength(2),
    Validators.required,
  ]);
  /**
   * FormControl for the login date of birth month.
   *
   * @type {FormControl}
   * @memberof TogetherAuctionDIPLoginComponent
   */
  tadLoginDobMonth: UntypedFormControl = new UntypedFormControl(null, [
    Validators.min(1),
    Validators.max(12),
    Validators.minLength(1),
    Validators.maxLength(2),
    Validators.required,
  ]);

  /**
   * FormControl for the login date of birth year.
   *
   * @type {FormControl}
   * @memberof TogetherAuctionDIPLoginComponent
   */
  tadLoginDobYear: UntypedFormControl = new UntypedFormControl(null, [
    Validators.minLength(4),
    Validators.maxLength(4),
    Validators.required,
  ]);

  /**
   * FormControl containing the full date of birth.
   *
   * @type {FormControl}
   * @memberof TogetherAuctionDIPLoginComponent
   */
  tadLoginDob: UntypedFormControl = new UntypedFormControl(null, [
    this.validateCorrectDate(),
  ]);


  /**
   * FormControl containing the email address.
   *
   * @type {FormControl}
   * @memberof TogetherAuctionDIPLoginComponent
   */
  tadLoginEmailAddress: UntypedFormControl = new UntypedFormControl(null, [
    Validators.required,
    // tslint:disable-next-line:max-line-length
    Validators.pattern(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/),
  ]);

  /**
   * FormControl containing the reference number.
   *
   * @type {FormControl}
   * @memberof TogetherAuctionDIPLoginComponent
   */
  tadLoginReferenceNumber: UntypedFormControl = new UntypedFormControl(null, [
    Validators.required,
    Validators.minLength(8),
  ]);

  /**
   * The login form.
   *
   * @type {FormGroup}
   * @memberof TogetherAuctionDIPLoginComponent
   */
  tadLoginForm: UntypedFormGroup = new UntypedFormGroup({
    tadLoginEmailAddress: this.tadLoginEmailAddress,
    tadLoginDobMonth: this.tadLoginDobMonth,
    tadLoginDobYear: this.tadLoginDobYear,
    tadLoginDobDay: this.tadLoginDobDay,
    tadLoginDob: this.tadLoginDob,
    tadLoginReferenceNumber: this.tadLoginReferenceNumber,
  });

  /**
   * Has the login failed?
   *
   * @type {boolean}
   * @memberof TogetherAuctionDIPLoginComponent
   */
  get loginFailed(): boolean {
    return this.state.loginFailed;
  }

  /**
   * Is the form loading?
   *
   * @type {boolean}
   * @memberof TogetherAuctionDIPLoginComponent
   */
  isFormLoading: boolean = false;


  /**
   * Checks to see if the result is invalid.
   *
   * @memberof TogetherAuctionDIPLoginComponent
   */
  isInvalidResult(result) {
    const error = _.defaultTo(result.Error, 'Unknown Error');
    return error === 'No case datails present for given Birthdate, Email and Reference';
  }

  /**
   * Does the login using JWT.
   *
   * @memberof TogetherAuctionDIPLoginComponent
   */
  doLogin() {
    this.isFormLoading = true;
    this.state.loginFailed = false;
    const dob = moment(this.tadLoginDob.value, 'YYYY MM DD');
    const output = {
      reference_number: _.toLower(this.tadLoginReferenceNumber.value).replace('q', ''),
      date_of_birth: dob,
      email_address: _.toLower(this.tadLoginEmailAddress.value),
    };
    const base64 = btoa(JSON.stringify(output));
    this.entity.getJWT(base64)
      .subscribe(
        (success) => {
          const jwt = (success as any).jwt;
          this.state.loginFailed = false;
          this.isFormLoading = false;
          this.router.navigate(['do_retrieve_case', jwt]);
        },
        (error) => {
          const errorInner = _.defaultTo(error.error, {});
          if (this.isInvalidResult(errorInner)) {
            this.state.loginFailed = true;
          } else {
            this.exception.haveReceivedError(errorInner);
          }
          this.isFormLoading = false;
        },
      );
  }

  /**
   * Handles forward clicked.
   *
   * @memberof TogetherAuctionDIPLoginComponent
   */
  onForwardClicked() {
    if (this.disableButtons() === false) {
      this.doLogin();
    } else {
      this.validateAllFormFields(this.tadLoginForm);
    }
  }
  /**
   * Reference to the RecaptchaComponent on the template
   *
   * @type {RecaptchaComponent}
   * @memberof TogetherAuctionDIPLoginComponent
   */
  @ViewChild('captchaRef') captchaRef: RecaptchaComponent;
  /**
   * Is ReCaptcha enabled for this environment
   *
   * @readonly
   * @type {boolean}
   * @memberof TogetherAuctionDIPLoginComponent
   */
  get isCaptchaEnabled(): boolean {
    return environment.captchaEnabled;
  }
  /**
   * Either call captcha if enabled
   *
   * @memberof TogetherAuctionDIPLoginComponent
   */
  doTryForwards(): void {
    if (this.isCaptchaEnabled) {
      this.captchaRef.execute();
    } else {
      this.onForwardClicked();
    }
  }
  /**
  * Validate all form fields on the form when submitted.
  *
  * @param {FormGroup} formGroup
  * @memberof TogetherAuctionDIPPropertyDetailsComponent
  */
  validateAllFormFields(formGroup: UntypedFormGroup) {
    // tslint:disable-next-line:no-this-assignment
    const self = this;
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof UntypedFormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof UntypedFormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }

  /**
   * Handles backward clicked.
   *
   * @memberof TogetherAuctionDIPLoginComponent
   */
  onBackwardClicked(backward) {
    if (this.disableButtons() === false) {
      this.stageChanging.emit(this.stage.current_stage.backward_reference);
    }
  }

  /**
   * Handles disabling buttons.
   *
   * @returns
   * @memberof TogetherAuctionDIPLoginComponent
   */
  disableButtons() {
    return this.tadLoginForm.invalid ? true : this.isFormLoading;
  }

  /**
   * Builds the DOB from the fields.
   *
   * @memberof TogetherAuctionDIPLoginComponent
   */
  buildDobFromFields() {

    // Get the value
    const dob = this.tadLoginForm.get('tadLoginDob').value;

    // Get the fields.
    const day = this.tadLoginForm.get('tadLoginDobDay');
    const month = this.tadLoginForm.get('tadLoginDobMonth');
    const year = this.tadLoginForm.get('tadLoginDobYear');

    // Get the date to check
    const date = year.value + ' ' + month.value + ' ' + day.value;

    // If the date is the same, stop a loop of statusChanges without unsubscribing.
    if (_.isNil(dob) || date !== dob) {
      this.tadLoginForm.get('tadLoginDob').setValue(date);
      this.tadLoginForm.get('tadLoginDob').updateValueAndValidity();
    }
  }

  dateFieldsValid() {
    // Get the fields.
    const day = this.tadLoginForm.get('tadLoginDobDay');
    const month = this.tadLoginForm.get('tadLoginDobMonth');
    const year = this.tadLoginForm.get('tadLoginDobYear');

    return day.valid && month.valid && year.valid;
  }

  /**
   * Subscribe to changes to prevent AfterContentCheck spam.
   *
   * @memberof TogetherAuctionDIPLoginComponent
   */
  subscribeToFormChanges() {
    // tslint:disable-next-line:prefer-const
    let self = this;
    this.tadLoginForm.statusChanges.subscribe((status) => {

      // If the fields are valid, then build the DOB.
      if (self.dateFieldsValid()) {
        // Build the DOB from the 3 fields.
        self.buildDobFromFields();
      }
    });
  }

  /**
   * Creates an instance of TogetherAuctionDIPLoginComponent.
   * @param {NodeAPIEntityService} entity Entity service.
   * @param {Router} router For checking changed routes and routing to outcome page.
   * @param {ActivatedRoute} route The route activated.
   * @param {DipStateService} state State service for checking state.
   * @memberof TogetherAuctionDIPLoginComponent
   */
  constructor(
    private entity: NodeAPIEntityService,
    private router: Router,
    private route: ActivatedRoute,
    private state: DipStateService,
    private exception: ExceptionService) {
    this.subscribeToFormChanges();
  }

}
